import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Filter, MoreHorizontal, Sparkles } from 'lucide-react';
import {
  ModernAppShell,
  ModernSecondaryNav,
  ModernLeftRail,
  ModernTopBar,
  ModernPageHeader,
  ModernCard,
  ModernCardHeader,
  ModernCardBody,
  ModernCardFooter,
  ModernButton,
  ModernStatusBadge,
  ModernActionTile,
  ModernSegmentedControl,
  ModernSettingsSection,
  ModernSettingsRow,
  ModernDataTableShell,
  ModernTableRow,
  AliceBottomLauncher,
  AliceDrawer,
  labMocks,
  type ModernSegment,
} from '@command-center/oneup-modern-ui';

const ALICE_AVATAR = '/MyNameIsNotAliceAvatar.png';

const viewSegments: ModernSegment<'board' | 'list' | 'calendar'>[] = [
  { value: 'list', label: 'List' },
  { value: 'board', label: 'Board' },
  { value: 'calendar', label: 'Calendar' },
];

/**
 * Design Lab — a safe, isolated preview of the experimental Modern UI.
 *
 * Renders with MOCK DATA ONLY (from the design-system package). It does not
 * touch real data, real actions, or any production page. Use it to review the
 * look-and-feel before wiring the Modern UI to real routes behind the flag.
 */
export function DesignLabView() {
  const [activeRail, setActiveRail] = useState('command-center');
  const [activeNav, setActiveNav] = useState('today');
  const [view, setView] = useState<'board' | 'list' | 'calendar'>('list');
  const [aliceOpen, setAliceOpen] = useState(false);
  const [composer, setComposer] = useState('');
  const [selected, setSelected] = useState<string[]>(['t1']);

  return (
    <div className="oneup-modern -mx-3 -my-3 min-h-full bg-background p-4 text-foreground sm:-mx-5 sm:-my-4 sm:p-6">
      {/* Lab header (outside the device frame) */}
      <div className="mx-auto mb-6 flex max-w-6xl flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/settings"
            className="mb-1.5 inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Back to Settings
          </Link>
          <h1 className="text-xl font-semibold tracking-[-0.01em]">One Up Modern UI — Design Lab</h1>
          <p className="mt-1 max-w-2xl text-[13px] text-muted-foreground">
            Isolated preview with mock data. The live portal is unaffected — this exists to review
            the system before any real page adopts it.
          </p>
        </div>
        <ModernStatusBadge tone="accent">Experimental · Preview</ModernStatusBadge>
      </div>

      {/* ===== App shell concept (device frame) ===== */}
      <Section title="App shell · rail · top command bar">
        <div className="relative mx-auto h-[640px] max-w-6xl overflow-hidden rounded-[var(--radius)] border border-border shadow-[var(--mui-shadow-raised)]">
          <ModernAppShell
            rail={
              <ModernLeftRail
                brand={
                  <span className="flex h-9 w-9 items-center justify-center rounded-[calc(var(--radius)-4px)] bg-primary text-[13px] font-bold text-primary-foreground">
                    1U
                  </span>
                }
                items={labMocks.mockRailItems}
                activeId={activeRail}
                onSelect={setActiveRail}
              />
            }
            secondaryNav={
              <ModernSecondaryNav
                header={
                  <div>
                    <div className="text-sm font-semibold">Command Center</div>
                    <div className="text-[11px] text-muted-foreground">One Up Digital</div>
                  </div>
                }
                groups={labMocks.mockNavGroups}
                activeId={activeNav}
                onSelect={setActiveNav}
              />
            }
            topBar={
              <ModernTopBar
                searchPlaceholder="Search tasks, clients, proposals…"
                onSearchClick={() => undefined}
                actions={
                  <ModernButton size="sm" variant="primary">
                    <Plus className="h-4 w-4" aria-hidden />
                    Quick Capture
                  </ModernButton>
                }
                trailing={
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-[12px] font-semibold text-muted-foreground">
                    DK
                  </span>
                }
              />
            }
          >
            {/* Workspace content */}
            <ModernPageHeader
              breadcrumbs={[{ label: 'Command Center' }, { label: 'Today' }]}
              title="Today"
              description="What needs your attention across every client."
              actions={
                <>
                  <ModernSegmentedControl
                    segments={viewSegments}
                    value={view}
                    onChange={setView}
                    size="sm"
                    aria-label="View mode"
                  />
                  <ModernButton size="sm" variant="outline">
                    <Filter className="h-4 w-4" aria-hidden />
                    Filter
                  </ModernButton>
                </>
              }
            />

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {labMocks.mockTiles.map((tile) => (
                <ModernActionTile key={tile.id} icon={tile.icon} label={tile.label} hint={tile.hint} />
              ))}
            </div>

            <div className="mt-5">
              <ModernDataTableShell
                title="Active tasks"
                columns={labMocks.mockTaskColumns}
                selectable
                toolbar={
                  <ModernButton size="sm" variant="ghost">
                    <MoreHorizontal className="h-4 w-4" aria-hidden />
                  </ModernButton>
                }
                footer={<span>{labMocks.mockTasks.length} tasks · 1 selected</span>}
              >
                {labMocks.mockTasks.map((task) => (
                  <ModernTableRow
                    key={task.id}
                    columns={labMocks.mockTaskColumns}
                    selectable
                    selected={selected.includes(task.id)}
                    onSelectChange={(on) =>
                      setSelected((ids) =>
                        on ? [...ids, task.id] : ids.filter((id) => id !== task.id),
                      )
                    }
                    onClick={() => undefined}
                    cells={{
                      name: <span className="font-medium text-foreground">{task.name}</span>,
                      client: task.client,
                      status: (
                        <ModernStatusBadge tone={labMocks.mockStatusTone[task.status]}>
                          {task.status}
                        </ModernStatusBadge>
                      ),
                      owner: task.owner,
                      due: task.due,
                    }}
                  />
                ))}
              </ModernDataTableShell>
            </div>
          </ModernAppShell>

          {/* Assistant launcher / drawer, contained inside the frame */}
          {aliceOpen ? (
            <AliceDrawer
              variant="floating"
              position="absolute"
              avatarSrc={ALICE_AVATAR}
              status="online"
              contextLabel="Context: Today · 5 tasks"
              messages={labMocks.mockAliceMessages}
              suggestions={labMocks.mockAliceSuggestions}
              actions={labMocks.mockAliceActions}
              composerValue={composer}
              onComposerChange={setComposer}
              onSend={() => setComposer('')}
              onClose={() => setAliceOpen(false)}
            />
          ) : (
            <AliceBottomLauncher
              position="absolute"
              avatarSrc={ALICE_AVATAR}
              status="online"
              unread={1}
              onClick={() => setAliceOpen(true)}
            />
          )}
        </div>
      </Section>

      {/* ===== Buttons ===== */}
      <Section title="Buttons">
        <ModernCard>
          <ModernCardBody className="flex flex-wrap items-center gap-2">
            <ModernButton variant="primary">Primary</ModernButton>
            <ModernButton variant="secondary">Secondary</ModernButton>
            <ModernButton variant="outline">Outline</ModernButton>
            <ModernButton variant="ghost">Ghost</ModernButton>
            <ModernButton variant="subtle">Subtle</ModernButton>
            <ModernButton variant="destructive">Destructive</ModernButton>
            <ModernButton variant="primary" size="sm">
              Small
            </ModernButton>
            <ModernButton variant="primary" size="lg">
              Large
            </ModernButton>
          </ModernCardBody>
        </ModernCard>
      </Section>

      {/* ===== Badges ===== */}
      <Section title="Status badges (quiet, muted)">
        <ModernCard>
          <ModernCardBody className="flex flex-wrap items-center gap-2">
            <ModernStatusBadge tone="neutral">Draft</ModernStatusBadge>
            <ModernStatusBadge tone="accent">In review</ModernStatusBadge>
            <ModernStatusBadge tone="info">In progress</ModernStatusBadge>
            <ModernStatusBadge tone="warning">Waiting on client</ModernStatusBadge>
            <ModernStatusBadge tone="success">Done</ModernStatusBadge>
            <ModernStatusBadge tone="danger">Blocked</ModernStatusBadge>
            <ModernStatusBadge tone="outline" dot={false}>
              Archived
            </ModernStatusBadge>
          </ModernCardBody>
        </ModernCard>
      </Section>

      {/* ===== Cards ===== */}
      <Section title="Cards (header · body · footer)">
        <div className="grid gap-3 md:grid-cols-2">
          <ModernCard>
            <ModernCardHeader
              title="Northwind Co"
              description="Retainer · since 2023"
              icon={<Sparkles className="h-4 w-4" aria-hidden />}
              actions={<ModernStatusBadge tone="success">Active</ModernStatusBadge>}
            />
            <ModernCardBody className="text-[13px] text-muted-foreground">
              Consistent card anatomy — every module shares this header / body / footer rhythm so
              pages stop drifting apart.
            </ModernCardBody>
            <ModernCardFooter>
              <span className="text-[12px] text-muted-foreground">Updated 2h ago</span>
              <ModernButton size="sm" variant="subtle">
                Open
              </ModernButton>
            </ModernCardFooter>
          </ModernCard>

          <ModernActionTile
            icon={<Sparkles className="h-5 w-5" aria-hidden />}
            label="Action tile"
            hint="Used for dashboards and homebases. Restrained accent, soft lift on hover."
            badge={<ModernStatusBadge tone="accent">New</ModernStatusBadge>}
          />
        </div>
      </Section>

      {/* ===== Settings ===== */}
      <Section title="Settings section · rows">
        <ModernSettingsSection
          title="Workspace"
          description="Organized, table-like rows — one consistent settings pattern."
          icon={<Sparkles className="h-4 w-4" aria-hidden />}
        >
          <ModernSettingsRow
            label="Design system mode"
            description="Switch between the current UI and the experimental Modern UI."
            control={<ModernStatusBadge tone="accent">Modern</ModernStatusBadge>}
          />
          <ModernSettingsRow
            label="Default view"
            description="How task lists open by default."
            control={
              <ModernSegmentedControl
                segments={viewSegments}
                value={view}
                onChange={setView}
                size="sm"
              />
            }
          />
          <ModernSettingsRow
            label="Weekly digest"
            description="Email summary every Monday."
            control={<ModernButton size="sm" variant="outline">Configure</ModernButton>}
          />
        </ModernSettingsSection>
      </Section>

      {/* ===== Assistant (static, expanded) ===== */}
      <Section title="Alice — minimized launcher & expanded drawer">
        <div className="grid gap-3 lg:grid-cols-[1fr_22rem]">
          <ModernCard className="flex items-center justify-center p-8">
            <div className="relative h-16 w-full">
              <AliceBottomLauncher
                position="absolute"
                avatarSrc={ALICE_AVATAR}
                status="online"
                unread={2}
                onClick={() => undefined}
              />
            </div>
          </ModernCard>
          <div className="h-[32rem]">
            <AliceDrawer
              variant="docked"
              avatarSrc={ALICE_AVATAR}
              status="thinking"
              statusLabel="Thinking…"
              contextLabel="Context: Task · Publish June newsletter"
              messages={labMocks.mockAliceMessages}
              suggestions={labMocks.mockAliceSuggestions}
              actions={labMocks.mockAliceActions}
              composerValue={composer}
              onComposerChange={setComposer}
              onSend={() => setComposer('')}
              className="rounded-[var(--radius)] border border-border shadow-[var(--mui-shadow-card)]"
            />
          </div>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mx-auto mb-8 max-w-6xl">
      <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}
